import {
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactElement,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import clsx from 'clsx'
import { Dropdown as DsDropdown, Popover as DsPopover } from '@digdir/designsystemet-react'

// ---------------------------------------------------------------------------
// Dropdown / Popover — presentational overlay wrappers (props in → UI out).
//
// Designsystemet owns the native manual-popover surface/top layer. Existing
// classes remain layout/content hooks:
//   Dropdown → .dd (trigger wrap) + .dd__menu (panel) + .dd__item (items)
//   Popover  → .popover (panel) + .popover-backdrop (click-catcher)
//
// The wrapper preserves the existing interaction contract:
//   - the trigger carries aria-haspopup + aria-expanded (+ aria-controls)
//   - the panel opens on trigger activation and closes on outside-click,
//     Escape, and (Dropdown) item selection
//   - focus moves into the panel on open and returns to the trigger on close
//   - Dropdown gives its menu role="menu" with roving focus across the
//     focusable items (Arrow keys / Home / End); Popover is the same shell
//     with arbitrary panel content and NO menu/listbox semantics
//
// Both are dumb and surface-agnostic: no data fetching, no privileged logic,
// no Norwegian strings — safe for the public, sessionless register bundle.
// Position is computed from the trigger's bounding box and applied as inline
// top/left (layout only — never color); the panel is fixed-positioned so it
// escapes overflow-clipping ancestors once portaled to <body>.
// ---------------------------------------------------------------------------

export type Placement = 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end'

export interface OverlayBaseProps {
  /** The anchor element that toggles the panel (e.g. a <Button>). */
  trigger: ReactNode
  /** Menu items / panel content. */
  children: ReactNode
  /** Controlled open state. When set, the component does not own open/close. */
  open?: boolean
  /** Initial open state for the uncontrolled case. */
  defaultOpen?: boolean
  /** Notified whenever the panel wants to open or close. */
  onOpenChange?: (open: boolean) => void
  /** Which corner the panel anchors to. Defaults to 'bottom-start'. */
  placement?: Placement
  /** Accessible name for a content popover dialog. */
  ariaLabel?: string
}

export type DropdownProps = OverlayBaseProps
export type PopoverProps = OverlayBaseProps

// Gap in px between the trigger and the floating panel.
const OFFSET = 6

// Selector for the elements roving focus walks inside a Dropdown menu.
const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'

type PanelStyle = Pick<CSSProperties, 'position' | 'top' | 'left' | 'right' | 'zIndex'>

// Compute the fixed-position coordinates for the panel from the trigger rect
// and the requested placement. Returns viewport coordinates (position: fixed).
function computePosition(
  triggerRect: DOMRect,
  panelEl: HTMLElement | null,
  placement: Placement,
): PanelStyle {
  const panelW = panelEl?.offsetWidth ?? 0
  const panelH = panelEl?.offsetHeight ?? 0

  const isTop = placement === 'top-start' || placement === 'top-end'
  const isEnd = placement === 'bottom-end' || placement === 'top-end'

  const top = isTop
    ? triggerRect.top - panelH - OFFSET
    : triggerRect.bottom + OFFSET
  const left = isEnd ? triggerRect.right - panelW : triggerRect.left

  // The JS-computed `left` is authoritative for every placement (it already
  // right-aligns end placements via `triggerRect.right - panelW`). Pin `right`
  // to auto so the `.dd__menu--right` modifier's `right: 0` cannot combine with
  // this inline `left` and stretch the fixed panel to the full viewport width.
  return {
    position: 'fixed',
    top: Math.round(Math.max(8, Math.min(top, window.innerHeight - panelH - 8))),
    left: Math.round(Math.max(8, Math.min(left, window.innerWidth - panelW - 8))),
    right: 'auto',
  }
}

// Shared overlay engine behind both Dropdown and Popover. `menu` toggles the
// menu role + roving focus (Dropdown) vs. a plain content panel (Popover).
// Exported for Popover.tsx; not part of the public design-system surface
// (the barrel re-exports only Dropdown and Popover).
export function Overlay({
  trigger,
  children,
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  placement = 'bottom-start',
  ariaLabel,
  menu,
}: OverlayBaseProps & { menu: boolean }) {
  const isControlled = controlledOpen !== undefined
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen)
  const open = isControlled ? controlledOpen : uncontrolledOpen

  const panelId = useId()
  // The `.dd` wrapper element we fully control — used as the positioning
  // anchor and as the outside-click exclusion zone for the trigger. We do NOT
  // rely on the caller's trigger forwarding a ref (React 19 function
  // components don't forward refs automatically, and our wrappers don't opt
  // in), so we measure the wrapper rather than the trigger element itself.
  const anchorRef = useRef<HTMLSpanElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const [portalHost, setPortalHost] = useState<Element>(document.body)
  const pointerDismissedRef = useRef<EventTarget | null>(null)
  const ignoreDsCloseRef = useRef(false)
  // Remembers the element to restore focus to when the panel closes.
  const restoreFocusRef = useRef<HTMLElement | null>(null)
  const [panelStyle, setPanelStyle] = useState<PanelStyle>({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 'auto',
  })

  const setOpen = useCallback(
    (next: boolean) => {
      if (next) {
        // Capture the element to restore focus to on close. The contract is
        // "focus returns to the trigger", so prefer the trigger element (first
        // focusable inside the .dd anchor); fall back to whatever currently
        // holds focus when the trigger is not itself focusable.
        restoreFocusRef.current =
          anchorRef.current?.querySelector<HTMLElement>(FOCUSABLE) ??
          (document.activeElement as HTMLElement | null)
      }
      if (!isControlled) setUncontrolledOpen(next)
      onOpenChange?.(next)
    },
    [isControlled, onOpenChange],
  )

  const toggle = useCallback(() => setOpen(!open), [open, setOpen])
  // DS keeps its document listeners until open changes; use the current owner.
  const requestCloseRef = useRef(setOpen)
  useLayoutEffect(() => { requestCloseRef.current = setOpen }, [setOpen])

  useLayoutEffect(() => {
    setPortalHost(anchorRef.current?.closest('[popover],dialog') ?? document.body)
    // Register before DS's passive document listeners, and retain that order
    // across placement changes. The trigger owns its toggle; pointerdown and
    // its following click are one close request even when the owner refuses.
    const onClick = (e: MouseEvent) => {
      ignoreDsCloseRef.current = !!anchorRef.current?.contains(e.target as Node) ||
        pointerDismissedRef.current === e.target
      pointerDismissedRef.current = null
    }
    document.addEventListener('click', onClick, true)
    return () => document.removeEventListener('click', onClick, true)
  }, [])

  // Reposition the panel against the anchor. Runs on open and on the
  // scroll/resize that would otherwise leave the panel stranded.
  const reposition = useCallback(() => {
    const anchorEl = anchorRef.current
    // A native popover is display:none until DS's passive effect opens it.
    if (!anchorEl || !panelRef.current?.matches(':popover-open')) return
    const rect = anchorEl.getBoundingClientRect()
    const modal = anchorEl.closest('.modal-bg,dialog')
    const modalLayer = modal ? Number(getComputedStyle(modal).zIndex) : 0
    setPanelStyle({ ...computePosition(rect, panelRef.current, placement), zIndex: modal?.tagName === 'DIALOG' ? 1000 : modalLayer ? modalLayer + 2 : undefined })
  }, [placement])

  // Keep the native surface pinned while open.
  useLayoutEffect(() => {
    if (!open) return
    reposition()
    window.addEventListener('resize', reposition)
    window.addEventListener('scroll', reposition, true)
    return () => {
      window.removeEventListener('resize', reposition)
      window.removeEventListener('scroll', reposition, true)
    }
  }, [open, reposition, portalHost])

  // Move focus into the panel on open; restore it to the opener on close.
  useEffect(() => {
    if (open) {
      // DS opens natively after layout effects; measure the visible surface.
      reposition()
      const panel = panelRef.current
      if (!panel) return
      // For the controlled case the parent may flip `open` without going
      // through setOpen, so seed the restore target from the trigger here if
      // it has not been captured yet.
      if (!restoreFocusRef.current) {
        restoreFocusRef.current =
          anchorRef.current?.querySelector<HTMLElement>(FOCUSABLE) ?? null
      }
      if (menu) {
        const first = panel.querySelector<HTMLElement>(FOCUSABLE)
        ;(first ?? panel).focus()
      } else {
        // Popover: focus the panel container itself so Escape is captured and
        // the user lands inside the floating region.
        panel.focus()
      }
    } else if (restoreFocusRef.current) {
      restoreFocusRef.current.focus()
      restoreFocusRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, portalHost])

  // Close on outside pointer-down (clicks inside the anchor or panel are kept
  // open) and on Escape anywhere.
  useEffect(() => {
    if (!open) return
    const onPointerDown = (e: PointerEvent) => {
      pointerDismissedRef.current = null
      const target = e.target as Node
      if (anchorRef.current?.contains(target)) return
      if (panelRef.current?.contains(target)) return
      pointerDismissedRef.current = e.target
      setOpen(false)
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // This capture guard owns nested Escape, including declined handling.
        ignoreDsCloseRef.current = true
        const target = e.target instanceof Element ? e.target : null
        if (e.defaultPrevented || (target?.closest('dialog') ?? null) !== (anchorRef.current?.closest('dialog') ?? null)) return
        if (target?.closest('[role="combobox"][aria-expanded="true"]')) return
        const innerPanel = target?.closest('.popover,.dd__menu,[role="listbox"]')
        if (innerPanel && innerPanel !== panelRef.current) return
        e.preventDefault()
        e.stopPropagation()
        setOpen(false)
      }
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('keydown', onKeyDown, true)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('keydown', onKeyDown, true)
    }
  }, [open, setOpen])

  // Roving focus across the menu items (Dropdown only).
  const onMenuKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>) => {
      if (!menu) return
      if ((e.target as Element).closest('.dd__menu,.popover') !== panelRef.current) return
      const panel = panelRef.current
      if (!panel) return
      const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE))
      if (items.length === 0) return
      const currentIndex = items.indexOf(
        document.activeElement as HTMLElement,
      )

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault()
          e.stopPropagation()
          const next = currentIndex < 0 ? 0 : (currentIndex + 1) % items.length
          items[next]?.focus()
          break
        }
        case 'ArrowUp': {
          e.preventDefault()
          e.stopPropagation()
          const prev =
            currentIndex <= 0 ? items.length - 1 : currentIndex - 1
          items[prev]?.focus()
          break
        }
        case 'Home': {
          e.preventDefault()
          e.stopPropagation()
          items[0]?.focus()
          break
        }
        case 'End': {
          e.preventDefault()
          e.stopPropagation()
          items[items.length - 1]?.focus()
          break
        }
        default:
          break
      }
    },
    [menu],
  )

  // A Dropdown closes when an item is selected; clicks that land on an
  // interactive element inside the menu count as a selection. Popover panels
  // stay open (caller-controlled content).
  const onPanelClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // React portal events still bubble through the caller's clickable row.
      e.stopPropagation()
      if (!menu) return
      const interactive = (e.target as HTMLElement).closest(FOCUSABLE)
      if (interactive && panelRef.current?.contains(interactive)) {
        setOpen(false)
      }
    },
    [menu, setOpen],
  )

  // Decorate the caller-supplied trigger with the toggle handler + ARIA
  // wiring. We inject only props that reliably flow to the underlying DOM node
  // (onClick + aria-* spread through ...rest in our wrappers) — NOT a ref,
  // since the trigger may be a function component that doesn't forward one.
  // Positioning + outside-click anchor on the `.dd` wrapper below instead.
  const triggerAria = {
    'aria-haspopup': menu ? ('menu' as const) : ('dialog' as const),
    'aria-expanded': open,
    'aria-controls': open ? panelId : undefined,
  }

  let triggerNode: ReactNode
  if (isValidElement(trigger)) {
    const el = trigger as ReactElement<Record<string, unknown>>
    triggerNode = cloneElement(el, {
      onClick: (e: React.MouseEvent) => {
        if (el.props.disabled || (e.currentTarget as HTMLElement).matches(':disabled')) return
        ;(el.props.onClick as ((e: React.MouseEvent) => void) | undefined)?.(e)
        // Opening a panel means "open this panel", never also "activate
        // whatever sits behind me". Without this a trigger inside a clickable
        // container (a row menu in a Table row) opens the menu AND fires the
        // container's handler, which then navigates the menu away.
        e.stopPropagation()
        if (!e.defaultPrevented) toggle()
      },
      ...triggerAria,
    })
  } else {
    triggerNode = (
      <span
        onClick={(e) => {
          e.stopPropagation()
          toggle()
        }}
        {...triggerAria}
      >
        {trigger}
      </span>
    )
  }

  const Surface = menu ? DsDropdown : DsPopover
  const panel = open
    ? createPortal(
        <>
          {/* Popover ships a transparent backdrop so a click anywhere outside
              the panel closes it; Dropdown relies on the document pointerdown
              listener and a tighter visual menu, so it needs no backdrop. */}
          {!menu && (
            <div
              className="popover-backdrop"
              style={{ zIndex: panelStyle.zIndex ? Number(panelStyle.zIndex) - 1 : undefined }}
              aria-hidden="true"
              onClick={(e) => e.stopPropagation()}
            />
          )}
          <Surface
            ref={panelRef}
            id={panelId}
            open={open}
            placement="none"
            onClose={() => {
              if (!ignoreDsCloseRef.current) requestCloseRef.current(false)
            }}
            onToggle={reposition}
            className={clsx(
              menu ? 'dd__menu' : 'popover',
              menu && placement.endsWith('end') && 'dd__menu--right',
            )}
            style={panelStyle}
            role={menu ? 'menu' : ariaLabel ? 'dialog' : undefined}
            aria-label={ariaLabel}
            tabIndex={menu ? undefined : -1}
            onKeyDown={onMenuKeyDown}
            onClick={onPanelClick}
          >
            {children}
          </Surface>
        </>,
        portalHost,
      )
    : null

  return (
    <span className="dd" ref={anchorRef}>
      {triggerNode}
      {panel}
    </span>
  )
}

/**
 * Anchored menu: a trigger that toggles a portal-rendered `role="menu"` panel
 * of items. Opens on trigger activation; closes on outside-click, Escape, and
 * item selection. Arrow keys / Home / End rove focus across the items; focus
 * returns to the trigger on close.
 *
 * Wrap items in `.dd__item` (use `<button className="dd__item">`), with
 * `.dd__sep` / `.dd__label` for separators and group labels. Styling is
 * supplied entirely by `shared/styles/components.css`.
 *
 * @example
 * <Dropdown trigger={<Button icon="more" iconOnly aria-label={t('actions')} />}>
 *   <button type="button" className="dd__item" onClick={onRename}>{t('rename')}</button>
 *   <div className="dd__sep" />
 *   <button type="button" className="dd__item dd__item--danger" onClick={onDelete}>
 *     {t('delete')}
 *   </button>
 * </Dropdown>
 */
export function Dropdown(props: DropdownProps) {
  return <Overlay {...props} menu />
}
